import React, { useState } from "react";
import { useForm } from "react-hook-form";
import Icon from "../../../components/AppIcon";

const ContactForm = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    watch,
  } = useForm();

  const serviceOptions = [
    { value: "", label: "Select a service" },
    { value: "web-development", label: "Web Development" },
    { value: "mobile-development", label: "Mobile App Development" },
    { value: "cloud-solutions", label: "Cloud Solutions" },
    { value: "ai-ml", label: "AI & Machine Learning" },
    { value: "consulting", label: "IT Consulting" },
    { value: "maintenance", label: "Support & Maintenance" },
    { value: "other", label: "Other" },
  ];

  const budgetRanges = [
    { value: "", label: "Select budget range" },
    { value: "under-10k", label: "Under $10,000" },
    { value: "10k-25k", label: "$10,000 - $25,000" },
    { value: "25k-50k", label: "$25,000 - $50,000" },
    { value: "50k-100k", label: "$50,000 - $100,000" },
    { value: "over-100k", label: "Over $100,000" },
    { value: "discuss", label: "Prefer to discuss" },
  ];

  const timelineOptions = [
    { value: "", label: "Select timeline" },
    { value: "asap", label: "ASAP" },
    { value: "1-month", label: "Within 1 month" },
    { value: "2-3-months", label: "2-3 months" },
    { value: "3-6-months", label: "3-6 months" },
    { value: "6-months-plus", label: "6+ months" },
    { value: "flexible", label: "Flexible" },
  ];

  const onFormSubmit = async (data) => {
    setIsSubmitting(true);
    try {
      const response = await fetch(
        `${import.meta.env.VITE_API_URL}/contactUs`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(data),
        }
      );

      if (!response.ok) {
        throw new Error("Failed to submit form");
      }

      const result = await response.json();
      console.log("Form submitted:", result);

      reset();
      alert(result?.ResponseMessage || "Thank you! We’ll get back to you shortly.");
    } catch (err) {
      console.error(" Error:", err);
      alert("Something went wrong. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const messageLength = watch("message")?.length || 0;
  // Simulate API call
  // await new Promise(resolve => setTimeout(resolve, 2000));

  // console.log('Form submitted:', data);
  // reset();
  // setIsSubmitting(false);
  // onSubmit();
  return (
    <form onSubmit={handleSubmit(onFormSubmit)} className="space-y-6">
      {/* Name + Email */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium mb-2">Full Name *</label>
          <input
            type="text"
            {...register("name", {
              required: "Full name is required",
              minLength: { value: 2, message: "Name must be at least 2 chars" },
            })}
            className={`w-full px-4 py-3 border rounded-md ${errors.name ? "border-error" : "border-border"
              }`}
            placeholder="Enter your full name"
          />
          {errors.name && (
            <p className="mt-1 text-sm text-error flex items-center">
              <Icon name="AlertCircle" size={16} className="mr-1" />
              {errors.name.message}
            </p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium mb-2">
            Email Address *
          </label>
          <input
            type="email"
            {...register("email", {
              required: "Email address is required",
              pattern: {
                value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                message: "Please enter a valid email address",
              },
            })}
            className={`w-full px-4 py-3 border rounded-md ${errors.email ? "border-error" : "border-border"
              }`}
            placeholder="Enter your email address"
          />
          {errors.email && (
            <p className="mt-1 text-sm text-error flex items-center">
              <Icon name="AlertCircle" size={16} className="mr-1" />
              {errors.email.message}
            </p>
          )}
        </div>
      </div>

      {/* Phone + Company */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium mb-2">Phone Number</label>
          <input
            type="tel"
            {...register("phone", {
              required: "Phone number is required",
              pattern: {
                value: /^[+0-9() ]{7,20}$/,
                message: "Please enter a valid phone number",
              },
            })}
            onInput={(e) => {
              // Allow only digits, +, (, ), space
              e.target.value = e.target.value.replace(/[^0-9+() ]/g, "");
            }}
            className={`w-full px-4 py-3 border border-border rounded-md focus-ring ${errors.phone ? "border-error" : ""
              }`}
            placeholder="(+91) 9874566321"
          />



          {errors.phone && (
            <p className="mt-1 text-sm text-error flex items-center">
              <Icon name="AlertCircle" size={16} className="mr-1" />
              {errors.phone.message}
            </p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium mb-2">Company Name</label>
          <input
            type="text"
            {...register("company_name")}
            className="w-full px-4 py-3 border border-border rounded-md"
            placeholder="Enter your company name"
          />
        </div>
      </div>

      {/* Service */}
      <div>
        <label className="block text-sm font-medium mb-2">
          Service of Interest *
        </label>
        <select
          {...register("service", { required: "Please select a service" })}
          className={`w-full px-4 py-3 border rounded-md ${errors.service ? "border-error" : "border-border"
            }`}
        >
          {serviceOptions.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
        {errors.service && (
          <p className="mt-1 text-sm text-error flex items-center">
            <Icon name="AlertCircle" size={16} className="mr-1" />
            {errors.service.message}
          </p>
        )}
      </div>

      {/* Budget + Timeline */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium mb-2">
            Project Budget Range
          </label>
          <select
            {...register("budget")}
            className="w-full px-4 py-3 border rounded-md"
          >
            {budgetRanges.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium mb-2">
            Preferred Timeline
          </label>
          <select
            {...register("timeline")}
            className="w-full px-4 py-3 border rounded-md"
          >
            {timelineOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Message */}
      <div>
        <label className="block text-sm font-medium mb-2">
          Project Details *
        </label>
        <textarea
          rows={6}
          {...register("message", {
            required: "Please provide project details",
            minLength: {
              value: 1,
              message: "Please provide Description details",
            },
            maxLength: {
              value: 1000,
              message: "Message cannot exceed 1000 characters",
            },
          })}
          className={`w-full px-4 py-3 border rounded-md resize-vertical ${errors.message ? "border-error" : "border-border"
            }`}
          placeholder="Please describe your project requirements..."
        />
        <div className="flex justify-between items-center mt-2">
          {errors.message && (
            <p className="text-sm text-error flex items-center">
              <Icon name="AlertCircle" size={16} className="mr-1" />
              {errors.message.message}
            </p>
          )}
          <p
            className={`text-sm ${messageLength > 900 ? "text-warning" : "text-text-secondary"
              }`}
          >
            {messageLength}/1000
          </p>
        </div>
      </div>

      {/* Newsletter */}
      <div className="flex items-start space-x-3">
        <input
          type="checkbox"
          {...register("newsletter")}
          className="mt-1 h-4 w-4 border-border rounded"
        />
        <label className="text-sm text-text-secondary">
          I would like to receive updates via email.
        </label>
      </div>

      {/* Submit Button */}
      <div className="pt-4">
        <button
          type="submit"
          disabled={isSubmitting}
          className={`w-full flex items-center justify-center px-6 py-4 bg-primary text-white font-medium rounded-md ${isSubmitting ? "opacity-75 cursor-not-allowed" : "hover:bg-primary-700"
            }`}
        >
          {isSubmitting ? (
            <>
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-3"></div>
              Sending Message...
            </>
          ) : (
            <>
              <Icon name="Send" size={20} className="mr-2" />
              Send Message
            </>
          )}
        </button>
      </div>
    </form>
  );
};

export default ContactForm;
